import type {CalypsoCatalog} from "#pricing/catalogs/calypso/catalog"
import type {KeyedVariants} from "#pricing/catalogs/graph-helpers"
import {
    createAmountTriggeredPriceNode,
    createKeyedVariantPriceNodes,
    createPriceContainer,
    createVariantConditionedPriceNodes,
} from "#pricing/catalogs/graph-helpers"
import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {MeshAmountNode} from "#pricing/nodes/mesh-amount-node"
import type {MeshesNode} from "#pricing/nodes/meshes-node"
import type {PriceContainer} from "#pricing/nodes/price-container"

/**
 * Triggered by its mesh, the finish by the furniture finish variant. The catalog lists the unit in eight finishes, but
 * each range offers only a subset of them: the caller passes the finish map of its range, the number of finishes that
 * remain and the finishes to drop.
 */
export function createRimellaWcUnitContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
    expectedFinishCount: number,
    finishExclude?: string[],
): PriceContainer {
    const rimellaAmount = new MeshAmountNode({parameters: {name: "5646 - Rimella WC Unit 550 x 250 x 830mm", meshes: Meshes}})
    const rimellaUnits = createVariantConditionedPriceNodes(catalog, "5646", expectedFinishCount, finishConfigMap, rimellaAmount, finishExclude)

    return createPriceContainer("Rimella WC Unit", "Rimella WC Unit", rimellaUnits, true)
}

/**
 * Triggered by its mesh, the finish by the furniture finish variant. The catalog lists the unit in seven finishes; the caller passes the
 * finish map of its range, the number of finishes that remain and the finishes to drop.
 */
export function createQuadraWcUnitContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
    expectedFinishCount: number,
    finishExclude?: string[],
): PriceContainer {
    const quadraAmount = new MeshAmountNode({parameters: {name: "4788 - Quadra WC Unit 500 x 225 x 829mm", meshes: Meshes}})
    const quadraUnits = createVariantConditionedPriceNodes(catalog, "4788", expectedFinishCount, finishConfigMap, quadraAmount, finishExclude)

    return createPriceContainer("Quadra WC Unit", "Quadra WC Unit", quadraUnits, true)
}

/** The Riverdale back to wall pan (with seat) only comes in white, so mesh presence alone prices it. */
export function createRiverdaleBtwPanContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const panAmount = new MeshAmountNode({parameters: {name: "36.9325.01 - Riverdale BTW Pan c-w Collerina SC Seat Rimless", meshes: Meshes}})
    const pan = createAmountTriggeredPriceNode(catalog, "36.9325.01", panAmount)

    return createPriceContainer("Riverdale BTW Pan", "Riverdale BTW Pan", [pan], true)
}

/** A config variant that selects a flush button finish. */
export type FlushButtonFinishVariant = [ConfigurationGroupNode, string]

/**
 * The compact concealed cistern comes with a chrome flush button and is priced by mesh presence. Black and brass buttons are sold
 * separately and priced on top, triggered by the cistern mesh and the button finish. Each variant gets its own priced item node: a
 * template may take the button finish from different groups (e.g. the accessory finish group of the selected tap), only one of which is
 * active at a time. The mesh name is passed in because it differs between templates.
 *
 * Templates with an accessory finish funnel pass the button finish as keyed variants instead, one key per offered button (description
 * keys of the "Cistern Flush Button (3099LF)" rows, e.g. "Matt Black", "Brushed Brass", "Brushed Bronze", "Gunmetal").
 */
export function createCompactConcealedCisternContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    cisternMeshName: string,
    buttonFinishVariants: {mattBlack: FlushButtonFinishVariant[]; brushedBrass: FlushButtonFinishVariant[]} | KeyedVariants,
): PriceContainer {
    const cisternAmount = new MeshAmountNode({parameters: {name: cisternMeshName, meshes: Meshes}})
    const cistern = createAmountTriggeredPriceNode(catalog, "Compact Concealed Cistern", cisternAmount)

    if ("variantIdByKey" in buttonFinishVariants) {
        const buttons = [...buttonFinishVariants.variantIdByKey.keys()].flatMap((key) =>
            createKeyedVariantPriceNodes(catalog, ["Cistern Flush Button (3099LF)", key], 1, buttonFinishVariants, {amount: cisternAmount}),
        )
        return createPriceContainer("Compact Concealed Cistern", "Compact Concealed Cistern", [cistern, ...buttons], true)
    }

    const createButtons = (sku: string, variants: FlushButtonFinishVariant[]) =>
        variants.map(([group, variantId]) => {
            const [node] = createVariantConditionedPriceNodes(catalog, sku, 1, undefined, cisternAmount)
            node.addDependency(group, variantId, "")
            return node
        })

    return createPriceContainer(
        "Compact Concealed Cistern",
        "Compact Concealed Cistern",
        [cistern, ...createButtons("7083MBK", buttonFinishVariants.mattBlack), ...createButtons("7083BBR", buttonFinishVariants.brushedBrass)],
        true,
    )
}
